import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import api from '@/admin/lib/api'
import { toastApiError } from '@/shared/lib/api-error'
import {
  scheduleClaimStateRefresh,
  type ClaimDetailResponse,
} from '@/admin/hooks/use-claims'

// ── Types ────────────────────────────────────────────────

export interface DecisionMatrixCell {
  uuid: string
  bracket_key: string
  bracket_label: string
  outcome: 'APPROVE' | 'DENY' | 'GOODWILL' | 'PROCESS_DEFECTIVE' | 'NA'
  conditions: string
  order: number
}

export interface DecisionMatrixRow {
  uuid: string
  sub_type_key: string
  sub_type_label: string
  criteria: string
  visual_boundary: string
  order: number
  metadata: Record<string, unknown>
  cells: DecisionMatrixCell[]
}

export interface DecisionMatrix {
  uuid: string
  key: string
  name: string
  defect_type: string
  product_scope: string
  description: string
  version: number
  is_active: boolean
  metadata: Record<string, unknown>
  rows: DecisionMatrixRow[]
}

export interface DecisionMatrixListItem {
  uuid: string
  key: string
  name: string
  defect_type: string
  product_scope: string
  version: number
  is_active: boolean
}

export interface LabTask {
  uuid: string
  claim_uuid: string
  task_type: 'EVALUATION' | 'TESTING'
  status: 'REQUESTED' | 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETE' | 'CANCELLED'
  priority: 'HIGH' | 'STANDARD' | 'LOW'
  title: string
  description: string
  assigned_to: number | null
  assigned_to_name: string | null
  assigned_at: string | null
  started_at: string | null
  completed_at: string | null
  photos_verified: boolean
  photos_verified_at: string | null
  photos_verified_by_name: string | null
  determination: string
  result_summary: string
  notes: string
  order: number
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface LabEvaluation {
  uuid: string
  matrix: string | null
  matrix_key: string
  matrix_name: string
  selected_row: string | null
  selected_row_label: string
  selected_cell: string | null
  selected_cell_outcome: string
  bracket_key: string
  outcome: string
  observations: Record<string, string>
  visual_boundary_match: string
  ventilation_adequate: boolean | null
  additional_notes: string
  completed_at: string | null
  created_at: string
  updated_at: string
}

export interface LabTestResult {
  uuid: string
  test_code: string
  test_name: string
  standard: string
  specimen_count: number
  set_point: string
  result_value: number | null
  result_unit: string
  pass_threshold: number | null
  pass_fail: string
  notes: string
  order: number
  raw_data: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface LabTestDefinition {
  uuid: string
  test_code: string
  test_name: string
  standard: string
  product_scope: string
  default_specimen_count: number
  default_set_point: string
  default_unit: string
  pass_threshold: number | null
  is_active: boolean
  order: number
  metadata: Record<string, unknown>
}

export interface LabSample {
  uuid: string
  sample_id: string
  // WPS-58: three-state model, driven by the sample_lifecycle workflow.
  status: 'REQUESTED' | 'RECEIVED' | 'REVIEWED'
  description: string
  // WPS-151: captured on the Add Sample popup in place of `description`,
  // which is retained so pre-WPS-151 samples still render their text.
  product: string
  received_date: string | null
  received_by: number | null
  received_by_name: string | null
  // WPS-151: written by the sample_lifecycle `review` action.
  reviewed_date: string | null
  condition: string
  meets_min_size: boolean | null
  plant_code: string
  // WPS-151 retest: the active freeform production field captured at Add Sample.
  production_code: string
  // Legacy: an earlier WPS-151 pass captured production as a date; kept for rows
  // logged then. Superseded by `production_code`.
  production_date: string | null
  location: string
  notes: string
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface EvidencePin {
  uuid: string
  x_pct: number
  y_pct: number
  comment: string
  pin_number: number
  created_by_role: string
  created_by_name: string | null
  created_at: string
  updated_at: string
}

export interface EvidenceAnnotation {
  uuid: string
  evidence_file: string
  evidence_file_uuid: string
  file_name: string
  file_url: string | null
  lab_task: string | null
  is_flagged: boolean
  flag_category: string
  notes: string
  annotated_by: number | null
  annotated_by_name: string | null
  pins: EvidencePin[]
  created_at: string
  updated_at: string
}

export interface LabQueueItem {
  claim_uuid: string
  claim_ref: string
  claim_status: string
  claimant_name: string
  product_category: string
  defect_type: string
  lab_task_uuid: string | null
  lab_task_status: string | null
  lab_task_type: string | null
  priority: string | null
  assigned_to_name: string | null
  days_in_lab: number | null
  created_at: string
  updated_at: string | null
}

export interface LabRequest {
  uuid: string
  request_type: 'ADDITIONAL_PHOTOS' | 'INSPECTION'
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
  reason: string
  reason_label: string
  notes: string
  requested_by: number | null
  requested_by_name: string | null
  assigned_to: number | null
  assigned_to_name: string | null
  completed_at: string | null
  response_notes: string
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface LabData {
  tasks: LabTask[]
  samples: LabSample[]
  annotations: EvidenceAnnotation[]
  evaluation: LabEvaluation | null
  test_results: LabTestResult[]
  requests: LabRequest[]
}

// ── Decision Matrix Queries ─────────────────────────────
//
// Two layers live side-by-side during PR #395 assimilation Phase A:
//   * Legacy: ``useDecisionMatrices`` / ``useDecisionMatrix`` read the
//     bespoke ``DecisionMatrix`` Django model via ``/decision-matrices/``.
//   * Rule-driven: ``useLabResolutionMatrices`` /
//     ``useEvaluateLabResolution`` call the new
//     ``/claims/<uuid>/lab-resolution/*`` endpoints that read
//     ``ResolutionRule`` rows under ``apps.claims``.
//
// The lab workspace migrates to the rule-driven hooks; the legacy ones
// are removed once Phase B drops the ``DecisionMatrix*`` models.

export function useDecisionMatrices() {
  return useQuery({
    queryKey: ['decision-matrices'],
    queryFn: async () => {
      const res = await api.get('/claims/decision-matrices/')
      return (res.data?.data ?? res.data) as DecisionMatrixListItem[]
    },
  })
}

// ── Dynamic checklist (per-defect, resolved server-side) ──

export interface ClaimChecklistItem {
  /** ``sample`` is mailed to the lab: shown, but never outstanding. */
  kind: 'photo' | 'document' | 'sample'
  key: string
  label: string
  description: string
  is_required: boolean
  category: string
  satisfied: boolean
  /**
   * WPS-140 #3: a reviewer has signed off this item's category as having
   * received sufficient evidence. Independent of `satisfied` (which only means
   * "an upload with this category exists").
   */
  approved: boolean
  /** The evidence behind this row, so a reviewer can see what they sign off. */
  files?: ChecklistItemFile[]
}

export interface ChecklistItemFile {
  uuid: string
  file_name: string
  file_url: string | null
  mime_type: string
  uploaded_at: string | null
}

export interface ChecklistRequestReason {
  value: string
  label: string
}

/**
 * A photo category from the tenant-wide catalogue (every defect's photo
 * requirements), used by "Request Additional Photos" so lab can request a
 * category beyond the claim's current defect (WPS-59). is_required_for_defect
 * marks the ones the current defect needs, so the picker pre-selects them.
 */
export interface ClaimPhotoCategory {
  key: string
  label: string
  description: string
  category: string
  is_required_for_defect: boolean
  satisfied: boolean
}

export interface ClaimChecklist {
  template_slug: string | null
  is_complete: boolean
  requires_sample: boolean
  // Reason catalogues served from the tenant-configurable lab_request.reason
  // entity attribute, scoped per surface (photos vs inspection).
  request_reasons: ChecklistRequestReason[]
  inspection_request_reasons: ChecklistRequestReason[]
  items: ClaimChecklistItem[]
  // Every photo category across the tenant's defects (WPS-59 Req 3).
  all_photo_categories: ClaimPhotoCategory[]
  // WPS-142 #17: recipients the "Email link to" field prepopulates with -
  // the homeowner (main contact) first, then external Contributor collaborators.
  default_recipients?: ChecklistRecipient[]
  // WPS-142 #11: photo categories requested repeatedly but still deficient, for
  // which a drone inspection is recommended. Empty unless the inspections flag
  // is on and a category has crossed the request threshold.
  drone_recommendations?: DroneRecommendation[]
  // WPS-142 #9/#17: proof documents a request sent now adds on its own
  // (optional for the customer); the picker shows them ticked and locked.
  auto_included_documents?: Array<{ key: string; label: string }>
}

export interface DroneRecommendation {
  category: string
  label: string
  request_count: number
}

export interface ChecklistRecipient {
  email: string
  name: string
  kind: 'homeowner' | 'contributor' | string
}

/**
 * The claim's dynamic checklist - per-defect required photos/documents
 * resolved from the same evidence-request template the email uses, each
 * flagged satisfied (evidence uploaded) or missing.
 */
export function useClaimChecklist(claimUuid: string | null | undefined) {
  return useQuery({
    queryKey: ['claim-checklist', claimUuid],
    enabled: !!claimUuid,
    queryFn: async () => {
      const res = await api.get(`/claims/claims/${claimUuid}/checklist/`)
      return (res.data?.data ?? res.data) as ClaimChecklist
    },
  })
}

export interface RequestChecklistInput {
  /** "additional" (Request Additional Photos) | "recheck" (Re-checklist). */
  mode: 'additional' | 'recheck'
  /** Requirement keys to checklist for. Optional for recheck (defaults to missing). */
  requirement_keys?: string[]
  reason?: string
  customer_note?: string
  /**
   * WPS-142 #17: "Email link to" addresses. First = primary To, rest are CC'd
   * on the same email (same upload link). Omitted -> main contact + collaborators.
   */
  recipient_emails?: string[]
}

export interface RequestChecklistResult {
  sent: boolean
  recipient_email: string
  recipient_name: string
  cc_emails?: string[]
  template_slug: string | null
  item_keys: string[]
  mode: string
}

/**
 * System-driven checklisting (WP1-59): sends a documentation checklist email
 * to the claim's main contact (consumer), scoped to the selected items
 * ("additional") or the missing items ("recheck"). Replaces the notify-RS relay.
 */
export function useRequestChecklist(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RequestChecklistInput) =>
      api
        .post(`/claims/claims/${claimUuid}/request-checklist/`, input)
        .then((r) => (r.data?.data ?? r.data) as RequestChecklistResult),
    onError: toastApiError,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-requests', claimUuid] })
      qc.invalidateQueries({ queryKey: ['claim-checklist', claimUuid] })
      // WPS-130: the request also moves the claim back to "Awaiting
      // Documents", so refresh the claim detail (workflow state + actions).
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
    },
  })
}

export interface SetCategoryApprovalInput {
  /** The checklist item's `category` key to (un)approve. */
  category: string
  /** true to approve, false to withdraw approval. */
  approved: boolean
}

/**
 * WPS-140 #3: toggle a reviewer's sign-off that an evidence category has
 * received sufficient evidence. Refreshes the checklist so the Lab tab Review
 * Checklist reflects the new "Approved" state. Gated server-side on
 * `claims.lab_actions`.
 */
export function useSetEvidenceCategoryApproval(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SetCategoryApprovalInput) =>
      api
        .post(`/claims/claims/${claimUuid}/evidence-category-approval/`, input)
        .then((r) => (r.data?.data ?? r.data) as SetCategoryApprovalInput),
    onError: toastApiError,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claim-checklist', claimUuid] })
    },
  })
}

export function useDecisionMatrix(key: string | null | undefined) {
  return useQuery({
    queryKey: ['decision-matrix', key],
    queryFn: async () => {
      const res = await api.get(`/claims/decision-matrices/${key}/`)
      return (res.data?.data ?? res.data) as DecisionMatrix
    },
    enabled: !!key,
  })
}

// ── Lab Resolution (rule-driven) ────────────────────────

/** Server shape returned by ``GET /api/claims/claims/<uuid>/lab-resolution/matrices/``. */
export interface LabResolutionRule {
  key: string
  name: string
  outcome: 'APPROVE' | 'DENY' | 'GOODWILL' | 'PROCESS_DEFECTIVE' | 'NA'
  sub_type_key: string
  sub_type_label: string
  bracket_key: string
  bracket_label: string
  conditions: string
  visual_boundary: string
  priority: number
}

export interface LabResolutionMatrix {
  matrix_key: string
  matrix_name: string
  defect_type: string
  rules: LabResolutionRule[]
}

/** Server shape returned by ``POST /api/claims/claims/<uuid>/lab-resolution/evaluate/``. */
export interface LabResolutionEvaluation {
  matched: boolean
  outcome: LabResolutionRule['outcome'] | null
  rule_key: string | null
  rule_name: string | null
  matrix_key?: string
  sub_type_key?: string
  bracket_key?: string
  conditions?: string
  metadata?: Record<string, unknown>
}

export function useLabResolutionMatrices(
  claimUuid: string | null | undefined,
  defectType?: string,
) {
  return useQuery({
    queryKey: ['lab-resolution-matrices', claimUuid, defectType ?? ''],
    queryFn: async () => {
      const params = defectType ? `?defect_type=${encodeURIComponent(defectType)}` : ''
      const res = await api.get(
        `/claims/claims/${claimUuid}/lab-resolution/matrices/${params}`,
      )
      return (res.data?.data ?? res.data) as LabResolutionMatrix[]
    },
    enabled: !!claimUuid,
  })
}

export function useEvaluateLabResolution(claimUuid: string | null | undefined) {
  return useMutation({
    mutationFn: async (input: {
      defect_type: string
      sub_type_key: string
      bracket_key: string
      extra_fields?: Record<string, unknown>
    }) => {
      const res = await api.post(
        `/claims/claims/${claimUuid}/lab-resolution/evaluate/`,
        input,
      )
      return (res.data?.data ?? res.data) as LabResolutionEvaluation
    },
  })
}

// ── Lab Test Definitions (component test catalog) ────────

export function useLabTestDefinitions(productScope?: string) {
  return useQuery({
    queryKey: ['lab-test-definitions', productScope],
    queryFn: async () => {
      const params = productScope ? `?product_scope=${productScope}` : ''
      const res = await api.get(`/claims/lab-test-definitions/${params}`)
      return (res.data?.data ?? res.data) as LabTestDefinition[]
    },
  })
}

// ── Lab Queue ───────────────────────────────────────────

/** One page of the Lab queue, with its status counts and Filters panel facets. */
export interface LabQueuePage {
  count: number
  results: LabQueueItem[]
  filter_options?: ProductFilterFacet[]
  /** Claims per lab task status (a claim with no task counts as REQUESTED). */
  status_counts?: Record<string, number>
  unassigned_count?: number
}

export function useLabQueue(params: Record<string, string>) {
  return useQuery({
    queryKey: ['lab-queue', params],
    queryFn: async () =>
      (await api.get<LabQueuePage>('/claims/lab-queue/', { params })).data,
    // The last page stays while the next loads, so the Filters panel's
    // facets (which ride on the page) do not blank mid-pick.
    placeholderData: keepPreviousData,
  })
}

// ── Lab Tasks ───────────────────────────────────────────

export function useLabTasks(claimUuid: string) {
  return useQuery({
    queryKey: ['lab-tasks', claimUuid],
    queryFn: async () => {
      const res = await api.get(`/claims/claims/${claimUuid}/lab-tasks/`)
      return (res.data?.data ?? res.data) as LabTask[]
    },
    enabled: !!claimUuid,
  })
}

export function useCreateLabTask(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .post(`/claims/claims/${claimUuid}/lab-tasks/`, data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-tasks', claimUuid] })
      qc.invalidateQueries({ queryKey: ['lab-queue'] })
    },
  })
}

export function useUpdateLabTask(claimUuid: string, taskUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .put(
          `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-tasks', claimUuid] })
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
    },
  })
}

export function useAssignLabTask(claimUuid: string, taskUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data?: { user_id?: number }) =>
      api
        .post(
          `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/assign/`,
          data ?? {}
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-tasks', claimUuid] })
      qc.invalidateQueries({ queryKey: ['lab-queue'] })
    },
  })
}

export function useTransitionLabTask(claimUuid: string, taskUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { target_status: string }) =>
      api
        .post(
          `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/transition/`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      const previousStateKey =
        qc.getQueryData<ClaimDetailResponse>(['claim', claimUuid])?.metadata
          ?.workflow_instance?.current_state_key ?? null
      qc.invalidateQueries({ queryKey: ['lab-tasks', claimUuid] })
      qc.invalidateQueries({ queryKey: ['lab-queue'] })
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
      // WPS-142 #7: a lab decision can trigger an asynchronous follow-up advance
      // (e.g. into Material & Labor Review); re-poll until the stage updates so
      // the detail page reflects it without a manual reload.
      scheduleClaimStateRefresh(qc, claimUuid, previousStateKey)
    },
  })
}

// ── Lab Evaluation ──────────────────────────────────────

export function useLabEvaluation(
  claimUuid: string,
  taskUuid: string | null | undefined
) {
  return useQuery({
    queryKey: ['lab-evaluation', claimUuid, taskUuid],
    queryFn: async () => {
      const res = await api.get(
        `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/evaluation/`
      )
      return (res.data?.data ?? res.data) as LabEvaluation
    },
    enabled: !!claimUuid && !!taskUuid,
  })
}

export function useSaveLabEvaluation(
  claimUuid: string,
  taskUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const existing = qc.getQueryData<LabEvaluation>([
        'lab-evaluation',
        claimUuid,
        taskUuid,
      ])
      if (existing?.uuid) {
        return api
          .put(
            `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/evaluation/`,
            data
          )
          .then((r) => r.data)
      }
      return api
        .post(
          `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/evaluation/`,
          data
        )
        .then((r) => r.data)
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['lab-evaluation', claimUuid, taskUuid],
      })
    },
  })
}

export function useCompleteLabEvaluation(
  claimUuid: string,
  taskUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api
        .post(
          `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/evaluation/complete/`
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['lab-evaluation', claimUuid, taskUuid],
      })
      qc.invalidateQueries({ queryKey: ['lab-tasks', claimUuid] })
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
    },
  })
}

// ── Lab Test Results ────────────────────────────────────

export function useLabTestResults(
  claimUuid: string,
  taskUuid: string | null | undefined
) {
  return useQuery({
    queryKey: ['lab-test-results', claimUuid, taskUuid],
    queryFn: async () => {
      const res = await api.get(
        `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/test-results/`
      )
      return (res.data?.data ?? res.data) as LabTestResult[]
    },
    enabled: !!claimUuid && !!taskUuid,
  })
}

export function useCreateLabTestResult(
  claimUuid: string,
  taskUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .post(
          `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/test-results/`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['lab-test-results', claimUuid, taskUuid],
      })
    },
  })
}

export function useUpdateLabTestResult(
  claimUuid: string,
  taskUuid: string,
  resultUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .put(
          `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/test-results/${resultUuid}/`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['lab-test-results', claimUuid, taskUuid],
      })
    },
  })
}

export function useDeleteLabTestResult(
  claimUuid: string,
  taskUuid: string,
  resultUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api
        .delete(
          `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/test-results/${resultUuid}/`
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['lab-test-results', claimUuid, taskUuid],
      })
    },
  })
}

// ── Lab Samples ─────────────────────────────────────────

export function useLabSamples(claimUuid: string) {
  return useQuery({
    queryKey: ['lab-samples', claimUuid],
    queryFn: async () => {
      const res = await api.get(
        `/claims/claims/${claimUuid}/lab-samples/`
      )
      return (res.data?.data ?? res.data) as LabSample[]
    },
    enabled: !!claimUuid,
  })
}

export function useCreateLabSample(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .post(`/claims/claims/${claimUuid}/lab-samples/`, data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-samples', claimUuid] })
      // WPS-151 retest: adding a sample reconciles the lab-identified product
      // onto the claim, so refresh the claim so the Defect Details "Product
      // Name" reflects the new value without a reload.
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
    },
  })
}

export function useUpdateLabSample(
  claimUuid: string,
  sampleUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .put(
          `/claims/claims/${claimUuid}/lab-samples/${sampleUuid}/`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-samples', claimUuid] })
      // Editing a sample's product reconciles it onto the claim (WPS-151
      // retest), so refresh the claim too.
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
    },
  })
}

// WPS-58: useTransitionLabSample removed. Sample transitions run through the
// generic workflow engine (see the sample-tracking-card, which uses
// useWorkflowInstanceLookup + WorkflowActionMenu against resource_type
// "lab_sample").

// ── Evidence Annotations ────────────────────────────────

export function useEvidenceAnnotations(
  claimUuid: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['evidence-annotations', claimUuid],
    queryFn: async () => {
      const res = await api.get(
        `/claims/claims/${claimUuid}/evidence-annotations/`
      )
      return (res.data?.data ?? res.data) as EvidenceAnnotation[]
    },
    // The endpoint is behind the lab flag and 404s without it, so a caller
    // that knows the flag is off should not ask.
    enabled: !!claimUuid && (options?.enabled ?? true),
  })
}

export function useCreateEvidenceAnnotation(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .post(
          `/claims/claims/${claimUuid}/evidence-annotations/`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['evidence-annotations', claimUuid],
      })
    },
    onError: (e) => toastApiError(e, 'Failed to save annotation'),
  })
}

export function useUpdateEvidenceAnnotation(
  claimUuid: string,
  annotationUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .put(
          `/claims/claims/${claimUuid}/evidence-annotations/${annotationUuid}/`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['evidence-annotations', claimUuid],
      })
    },
    onError: (e) => toastApiError(e, 'Failed to update annotation'),
  })
}

export function useDeleteEvidenceAnnotation(
  claimUuid: string,
  annotationUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api
        .delete(
          `/claims/claims/${claimUuid}/evidence-annotations/${annotationUuid}/`
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['evidence-annotations', claimUuid],
      })
    },
    onError: (e) => toastApiError(e, 'Failed to delete annotation'),
  })
}

// ── Evidence Pins ──────────────────────────────────────

export function useCreateEvidencePin(
  claimUuid: string,
  annotationUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      x_pct: number
      y_pct: number
      comment?: string
      created_by_role?: string
    }) =>
      api
        .post(
          `/claims/claims/${claimUuid}/evidence-annotations/${annotationUuid}/pins/`,
          data
        )
        .then((r) => (r.data?.data ?? r.data) as EvidencePin),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['evidence-annotations', claimUuid],
      })
    },
    onError: (e) => toastApiError(e, 'Failed to add pin'),
  })
}

export function useUpdateEvidencePin(
  claimUuid: string,
  annotationUuid: string,
  pinUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { comment?: string; x_pct?: number; y_pct?: number }) =>
      api
        .put(
          `/claims/claims/${claimUuid}/evidence-annotations/${annotationUuid}/pins/${pinUuid}/`,
          data
        )
        .then((r) => (r.data?.data ?? r.data) as EvidencePin),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['evidence-annotations', claimUuid],
      })
    },
    onError: (e) => toastApiError(e, 'Failed to update pin'),
  })
}

export function useDeleteEvidencePin(
  claimUuid: string,
  annotationUuid: string,
  pinUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api
        .delete(
          `/claims/claims/${claimUuid}/evidence-annotations/${annotationUuid}/pins/${pinUuid}/`
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['evidence-annotations', claimUuid],
      })
    },
    onError: (e) => toastApiError(e, 'Failed to remove pin'),
  })
}

// ── Photo Verification ─────────────────────────────────

export function useVerifyPhotos(claimUuid: string, taskUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api
        .post(
          `/claims/claims/${claimUuid}/lab-tasks/${taskUuid}/verify-photos/`
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-tasks', claimUuid] })
      qc.invalidateQueries({ queryKey: ['lab-queue'] })
    },
  })
}

// ── Lab Requests ───────────────────────────────────────

export function useLabRequests(claimUuid: string) {
  return useQuery({
    queryKey: ['lab-requests', claimUuid],
    queryFn: async () => {
      const res = await api.get(
        `/claims/claims/${claimUuid}/lab-requests/`
      )
      return (res.data?.data ?? res.data) as LabRequest[]
    },
    enabled: !!claimUuid,
  })
}

export function useCreateLabRequest(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      request_type: 'ADDITIONAL_PHOTOS' | 'INSPECTION'
      reason: string
      notes?: string
    }) =>
      api
        .post(`/claims/claims/${claimUuid}/lab-requests/`, data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-requests', claimUuid] })
      // WPS-175 #5: the endpoint now drives the claim's workflow action
      // when one is configured, so a request can raise an escalation,
      // create an inspection and change what the claim shows. Refresh the
      // claim itself, not just the request list, or the Inspection tab and
      // the escalation banner stay stale until a reload.
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
      qc.invalidateQueries({ queryKey: ['inspections-claim-evidence', claimUuid] })
    },
  })
}

export function useUpdateLabRequest(
  claimUuid: string,
  requestUuid: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api
        .put(
          `/claims/claims/${claimUuid}/lab-requests/${requestUuid}/`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab-requests', claimUuid] })
    },
  })
}
